// src/modules/ida/common/htmlPreview.ts
//
// What? Shared utilities for HTML document preview with asset resolution and mmData injection.
// Why? Both DocumentPreviewModal and PublicDocumentViewer render HTML in iframes and need
//       identical fetch interception, asset resolution, and data mutation support.
// How? Extracts common functions used by both viewers into a single module.

export interface ResolvedAsset {
  data_uri: string;
  content_type: string;
  content?: string;
}

export type PreviewType =
  | "html"
  | "pdf"
  | "image"
  | "markdown"
  | "text"
  | "unsupported";

export const getPreviewType = (filename: string): PreviewType => {
  const ext = filename.toLowerCase().split(".").pop() || "";
  switch (ext) {
    case "html":
      return "html";
    case "pdf":
      return "pdf";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
      return "image";
    case "md":
      return "markdown";
    case "txt":
    case "csv":
    case "json":
      return "text";
    default:
      return "unsupported";
  }
};

export const getExtension = (filename: string): string => {
  return filename.toLowerCase().split(".").pop() || "";
};

/**
 * Extract relative asset filenames from HTML content.
 * Finds src="...", href="...", CSS url(), and fetch("...") references.
 * Skips absolute URLs, data URIs, anchors, and protocol-relative URLs.
 */
export const extractRelativeAssetFilenames = (html: string): string[] => {
  const filenames = new Set<string>();

  const patterns = [
    /\bsrc\s*=\s*["']([^"']+)["']/gi,
    /\bhref\s*=\s*["']([^"']+)["']/gi,
    /\burl\(\s*["']?([^"')]+)["']?\s*\)/gi,
    /\bfetch\(\s*["']([^"']+)["']\s*\)/gi,
  ];

  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(html)) !== null) {
      const ref = match[1].trim();
      if (
        ref.startsWith("http://") ||
        ref.startsWith("https://") ||
        ref.startsWith("data:") ||
        ref.startsWith("#") ||
        ref.startsWith("//") ||
        // eslint-disable-next-line no-script-url
        ref.startsWith("javascript:") ||
        ref.startsWith("mailto:")
      ) {
        continue;
      }
      filenames.add(ref);
    }
  }

  return Array.from(filenames);
};

/**
 * Replace relative asset references in HTML with data URIs.
 * Only replaces src="...", href="...", and CSS url() references.
 * fetch() calls are handled separately via the injected interceptor script.
 */
export const replaceAssetReferences = (
  html: string,
  assets: Record<string, ResolvedAsset>,
): string => {
  let result = html;

  for (const [filename, asset] of Object.entries(assets)) {
    const dataUri = asset.data_uri;
    const escaped = filename.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = new RegExp(
      `((?:src|href)\\s*=\\s*["'])([^"']*?${escaped})(["'])`,
      "gi",
    );
    result = result.replace(pattern, `$1${dataUri}$3`);

    const urlPattern = new RegExp(
      `(url\\(\\s*["']?)([^"')]*?${escaped})(["']?\\s*\\))`,
      "gi",
    );
    result = result.replace(urlPattern, `$1${dataUri}$3`);
  }

  return result;
};

/**
 * Inject fetch interceptor and mmData API into HTML content.
 * The interceptor serves prefetched assets from memory for fetch() calls.
 * The mmData API enables granular JSON data mutations via postMessage to the parent frame.
 */
export const injectFetchInterceptor = (
  html: string,
  assets: Record<string, ResolvedAsset>,
): string => {
  const fetchableAssets: Record<string, { body: string; contentType: string }> =
    {};
  for (const [filename, asset] of Object.entries(assets)) {
    if (asset.content !== undefined) {
      fetchableAssets[filename] = {
        body: asset.content,
        contentType: asset.content_type,
      };
    }
  }

  // Always inject -- even without fetchable assets we need mmData
  const script = `<script>
(function() {
  var __prefetched = ${JSON.stringify(fetchableAssets)};
  var __origFetch = window.fetch;

  // Build basename fallback index for backward compat (mmData uses basenames)
  var __basenameIndex = {};
  for (var key in __prefetched) {
    var bn = key.split('/').pop();
    if (bn && !__basenameIndex[bn]) __basenameIndex[bn] = key;
  }

  function __resolve(name) {
    if (__prefetched[name]) return __prefetched[name];
    var bn = name.split('/').pop();
    var resolved = __basenameIndex[bn];
    return resolved ? __prefetched[resolved] : null;
  }

  // --- Fetch interceptor: intercept GET requests for prefetched assets ---
  window.fetch = function(url, opts) {
    if (typeof url === 'string') {
      var asset = __resolve(url);
      if (asset) {
        return Promise.resolve(new Response(asset.body, {
          status: 200,
          headers: { 'Content-Type': asset.contentType }
        }));
      }
    }
    try {
      return __origFetch.apply(this, arguments);
    } catch(e) {
      return Promise.reject(e);
    }
  };

  // --- mmData: granular data mutation API ---
  var __pendingMutations = {};
  var __mutationId = 0;

  function __parsePath(path) {
    var segments = [];
    var re = /([^[.]+)|\\[(\\d+)\\]/g;
    var m;
    while ((m = re.exec(path)) !== null) {
      if (m[2] !== undefined) segments.push(parseInt(m[2], 10));
      else if (m[1] !== undefined) segments.push(m[1]);
    }
    return segments;
  }

  function __traverse(data, segments) {
    var current = data;
    for (var i = 0; i < segments.length - 1; i++) {
      current = current[segments[i]];
    }
    return { parent: current, key: segments[segments.length - 1] };
  }

  function __applyLocal(filename, op, path, value, index) {
    var asset = __resolve(filename);
    if (!asset) return;
    var data;
    try { data = JSON.parse(asset.body); } catch(e) { return; }
    var segments = __parsePath(path);

    if (op === 'set') {
      var ref = __traverse(data, segments);
      ref.parent[ref.key] = value;
    } else if (op === 'push') {
      var target = data;
      for (var i = 0; i < segments.length; i++) target = target[segments[i]];
      target.push(value);
    } else if (op === 'remove') {
      var target = data;
      for (var i = 0; i < segments.length; i++) target = target[segments[i]];
      target.splice(index, 1);
    } else if (op === 'merge') {
      var target = data;
      for (var i = 0; i < segments.length; i++) target = target[segments[i]];
      Object.assign(target, value);
    }

    asset.body = JSON.stringify(data);
    return data;
  }

  function __sendMutation(filename, op, path, value, index) {
    var id = ++__mutationId;
    var msg = {
      type: 'mmData',
      id: id,
      filename: filename,
      op: op,
      path: path,
      value: value,
      index: index
    };

    return new Promise(function(resolve, reject) {
      __pendingMutations[id] = {
        id: id,
        filename: filename,
        op: op,
        path: path,
        value: value,
        index: index,
        resolve: resolve,
        reject: reject
      };
      window.parent.postMessage(msg, '*');

      setTimeout(function() {
        if (__pendingMutations[id]) {
          delete __pendingMutations[id];
          reject(new Error('mmData persistence timed out'));
        }
      }, 15000);
    });
  }

  window.addEventListener('message', function(event) {
    if (event.source !== window.parent) return;
    var d = event.data;
    if (!d || d.type !== 'mmDataResponse') return;
    var pending = __pendingMutations[d.id];
    if (!pending || d.id !== pending.id) return;
    delete __pendingMutations[d.id];
    if (d.error) {
      pending.reject(new Error(d.error));
    } else if (d.filename !== pending.filename || d.data === undefined) {
      pending.reject(new Error('Invalid mmData persistence response'));
    } else {
      __applyLocal(
        pending.filename,
        pending.op,
        pending.path,
        pending.value,
        pending.index
      );
      var resolvedAsset = __resolve(pending.filename);
      if (resolvedAsset) resolvedAsset.body = JSON.stringify(d.data);
      pending.resolve(d.data);
    }
  });

  window.mmData = {
    set: function(filename, path, value) {
      return __sendMutation(filename, 'set', path, value, undefined);
    },
    push: function(filename, path, value) {
      return __sendMutation(filename, 'push', path, value, undefined);
    },
    remove: function(filename, path, index) {
      return __sendMutation(filename, 'remove', path, undefined, index);
    },
    merge: function(filename, path, value) {
      return __sendMutation(filename, 'merge', path, value, undefined);
    }
  };
})();
</script>`;

  if (html.includes("</head>")) {
    return html.replace("</head>", script + "</head>");
  } else if (html.includes("<body")) {
    return html.replace(/<body([^>]*)>/, `<body$1>${script}`);
  }
  return script + html;
};

/**
 * Full HTML processing pipeline: resolve assets and inject interceptor.
 * Takes raw HTML and resolved assets, returns processed HTML ready for iframe.
 */
export const processHtmlWithAssets = (
  html: string,
  assets: Record<string, ResolvedAsset>,
): string => {
  let processed = replaceAssetReferences(html, assets);
  processed = injectFetchInterceptor(processed, assets);
  return processed;
};
